/**
 * Default workflow configurations for image generation and editing
 * Priority determines which workflow to select when multiple are available
 * Higher priority = preferred choice
 */

export const DEFAULT_WORKFLOWS = {
  // Image editing workflows (accept image inputs)
  IMAGE_EDITING: [
    {
      id: 'stable-diffusion-img2img',
      name: 'Stable Diffusion Image-to-Image',
      model: 'stable-diffusion-v1-5',
      priority: 60,
      capabilities: ['image_input', 'image_output', 'prompt'],
      description: 'Transform existing images with text prompts',
      defaultParams: {
        strength: 0.75,
        guidance_scale: 7.5,
        num_inference_steps: 50,
        scheduler: 'DPMSolverMultistep'
      }
    },
    {
      id: 'sdxl-img2img',
      name: 'SDXL Image-to-Image',
      model: 'stable-diffusion-xl-base-1.0',
      priority: 55,
      capabilities: ['image_input', 'image_output', 'prompt', 'high_resolution'],
      description: 'High-quality image transformation with SDXL',
      defaultParams: {
        strength: 0.8,
        guidance_scale: 7.5,
        num_inference_steps: 30,
        scheduler: 'DPMSolverMultistep'
      }
    },
    {
      id: 'controlnet-canny',
      name: 'ControlNet Canny Edge',
      model: 'controlnet-canny',
      priority: 50,
      capabilities: ['image_input', 'image_output', 'prompt', 'edge_detection'],
      description: 'Generate images following edge structure',
      defaultParams: {
        guidance_scale: 7.5,
        num_inference_steps: 20,
        controlnet_conditioning_scale: 1.0
      }
    },
    {
      id: 'controlnet-depth',
      name: 'ControlNet Depth',
      model: 'controlnet-depth',
      priority: 45,
      capabilities: ['image_input', 'image_output', 'prompt', 'depth_map'],
      description: 'Generate images preserving depth structure',
      defaultParams: {
        guidance_scale: 7.5,
        num_inference_steps: 20,
        controlnet_conditioning_scale: 1.0
      }
    },
    {
      id: 'inpainting',
      name: 'Stable Diffusion Inpainting',
      model: 'stable-diffusion-inpainting',
      priority: 100,
      capabilities: ['image_input', 'mask_input', 'image_output', 'prompt'],
      description: 'Fill in or replace parts of an image',
      defaultParams: {
        guidance_scale: 7.5,
        num_inference_steps: 50,
        scheduler: 'DPMSolverMultistep'
      }
    },
    {
      id: 'instruct-pix2pix',
      name: 'InstructPix2Pix',
      model: 'instruct-pix2pix',
      priority: 40,
      capabilities: ['image_input', 'image_output', 'instruction'],
      description: 'Edit images using natural language instructions',
      defaultParams: {
        image_guidance_scale: 1.5,
        guidance_scale: 7.5,
        num_inference_steps: 20
      }
    }
  ],
  
  // Text-to-image workflows (no image input)
  TEXT_TO_IMAGE: [
    {
      id: 'stable-diffusion-txt2img',
      name: 'Stable Diffusion Text-to-Image',
      model: 'stable-diffusion-v1-5',
      priority: 100,
      capabilities: ['prompt', 'image_output'],
      description: 'Generate images from text descriptions',
      defaultParams: {
        guidance_scale: 7.5,
        num_inference_steps: 50,
        scheduler: 'DPMSolverMultistep',
        width: 512,
        height: 512
      }
    },
    {
      id: 'sdxl-txt2img',
      name: 'SDXL Text-to-Image',
      model: 'stable-diffusion-xl-base-1.0',
      priority: 95,
      capabilities: ['prompt', 'image_output', 'high_resolution'],
      description: 'High-quality image generation with SDXL',
      defaultParams: {
        guidance_scale: 7.5,
        num_inference_steps: 30,
        scheduler: 'DPMSolverMultistep',
        width: 1024,
        height: 1024
      }
    }
  ]
};

/**
 * Get the default image editing workflow
 * @returns {Object} Default workflow for image editing
 */
export const getDefaultImageEditingWorkflow = () => {
  return DEFAULT_WORKFLOWS.IMAGE_EDITING[0];
};

/**
 * Get all image-compatible workflows sorted by priority
 * @returns {Array} Array of image editing workflows
 */
export const getImageCompatibleWorkflows = () => {
  return [...DEFAULT_WORKFLOWS.IMAGE_EDITING].sort((a, b) => b.priority - a.priority);
};

/**
 * Find a workflow by ID across all categories
 * @param {string} workflowId - The workflow ID to find
 * @returns {Object|null} The workflow object or null if not found
 */
export const findWorkflowById = (workflowId) => {
  // Search in image editing workflows
  const imageWorkflow = DEFAULT_WORKFLOWS.IMAGE_EDITING.find(w => w.id === workflowId);
  if (imageWorkflow) return imageWorkflow;
  
  // Search in text-to-image workflows
  const textWorkflow = DEFAULT_WORKFLOWS.TEXT_TO_IMAGE.find(w => w.id === workflowId);
  if (textWorkflow) return textWorkflow;
  
  return null;
};

/**
 * Check if a workflow ID corresponds to an image-compatible workflow
 * @param {string} workflowId - The workflow ID to check
 * @returns {boolean} True if the workflow accepts image inputs
 */
export const isImageCompatibleWorkflowId = (workflowId) => {
  const workflow = DEFAULT_WORKFLOWS.IMAGE_EDITING.find(w => w.id === workflowId);
  return !!workflow;
};

/**
 * Get a workflow by model name
 * @param {string} modelName - The model name to search for
 * @returns {Object|null} The workflow object or null if not found
 */
export const findWorkflowByModel = (modelName) => {
  if (!modelName) return null;
  
  const normalizedModel = modelName.toLowerCase();
  
  // Search in all workflows
  const allWorkflows = [
    ...DEFAULT_WORKFLOWS.IMAGE_EDITING,
    ...DEFAULT_WORKFLOWS.TEXT_TO_IMAGE
  ];
  
  return allWorkflows.find(w => 
    w.model.toLowerCase() === normalizedModel ||
    w.id.toLowerCase() === normalizedModel
  ) || null;
};

/**
 * Get the best image editing workflow for a given model/workflow name
 * @param {string} currentModel - Current model or workflow name
 * @returns {Object} Best matching image editing workflow
 */
export const getBestImageEditingWorkflow = (currentModel) => {
  // If current model is already image-compatible, return it
  if (currentModel) {
    const existingWorkflow = findWorkflowByModel(currentModel);
    if (existingWorkflow && DEFAULT_WORKFLOWS.IMAGE_EDITING.includes(existingWorkflow)) {
      return existingWorkflow;
    }
  }
  
  // Otherwise return the highest priority image editing workflow
  return getDefaultImageEditingWorkflow();
};